'use client'

// Browser half of the member activity record (analytics round 5, 2026-10-17): what a signed-in
// member does (search, sheet view/download, vendor contact, save/unsave) is sent to EA's own
// server, POST /api/member-activity, which writes it to public.member_activity as that member.
// It runs next to the PostHog events, not instead of them, and does not depend on the cookie
// banner: nothing is stored on the device and nothing goes to a third party. The only thing it
// reads is the login session the browser Supabase client already keeps in its cookie; no
// session, no request. Fire-and-forget: never throws, never delays what the member is doing.
// Questions (forms) are recorded server-side in their own routes, not here.
import { SUPABASE_CONFIGURED, supabaseBrowser } from '@/lib/supabase/client'
import {
  MEMBER_ACTIVITY_LIMITS,
  isMemberAction,
  isSitePath,
  stripPath,
  type MemberAction,
} from './memberActivityShared'

export type { MemberAction } from './memberActivityShared'

export interface MemberActivityDetails {
  /** Sheet title or media path, vendor slug or channel, saved path. Never a person. */
  target?: string | null
  /** Searches only; trimmed to 200 characters. */
  searchText?: string | null
  /** Defaults to the current page path (read when called, before any navigation). */
  path?: string | null
}

// One action, one record: the same action with the same target and search text again within
// this window is skipped (a home-page search lands on /search, which fires its own event for
// the same query; double taps). Kept in memory only, so it lasts until the page is reloaded.
const REPEAT_WINDOW_MS = 30_000
let last: { key: string; at: number } | null = null

/** Test hook: forget the last recorded action. */
export function resetMemberActivityDedupe() {
  last = null
}

const clip = (value: string | null | undefined, max: number): string | undefined => {
  const v = typeof value === 'string' ? value.trim().slice(0, max).trim() : ''
  return v || undefined
}

export async function recordMemberActivity(
  action: MemberAction,
  details: MemberActivityDetails = {},
): Promise<void> {
  try {
    if (typeof window === 'undefined' || !SUPABASE_CONFIGURED || !isMemberAction(action)) return

    const rawPath = stripPath(details.path ?? window.location.pathname)
    const path =
      rawPath && isSitePath(rawPath) ? rawPath.slice(0, MEMBER_ACTIVITY_LIMITS.path) : undefined
    const target = clip(details.target, MEMBER_ACTIVITY_LIMITS.target)
    const searchText =
      action === 'search' ? clip(details.searchText, MEMBER_ACTIVITY_LIMITS.searchText) : undefined

    const key = JSON.stringify([action, target ?? '', searchText ?? ''])
    const now = Date.now()
    if (last && last.key === key && now - last.at < REPEAT_WINDOW_MS) return
    last = { key, at: now }

    const { data } = await supabaseBrowser().auth.getSession()
    if (!data.session) return

    await fetch('/api/member-activity', {
      method: 'POST',
      keepalive: true,
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, path, target, searchText }),
    })
  } catch {
    // best effort: a lost record must never break the page
  }
}
